import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, Save, Droplets, Plus, Minus, Camera, Loader2, ArrowLeft } from 'lucide-react';
import { API } from '@/lib/api';


const moodIcons = [':(', ':/', ':|', ':)', ':D'];
const moodColors = ['text-red-400', 'text-orange-400', 'text-yellow-500', 'text-green-400', 'text-emerald-500'];

export default function TrackingPage() {
  const { t, language } = useLanguage();
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState({
    weight: '', mood: 3, energy: 3, sleep_hours: '', water_glasses: 0, steps: '',
    meals: [
      { type: 'breakfast', description: '' },
      { type: 'lunch', description: '' },
      { type: 'dinner', description: '' },
      { type: 'snack', description: '' },
    ],
    habits: [
      { name: 'water_8', completed: false },
      { name: 'veggies', completed: false },
      { name: 'walk_10', completed: false },
      { name: 'mindful', completed: false },
      { name: 'protein', completed: false },
    ],
    notes: '',
  });
  const [mealAnalysis, setMealAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const handlePhotoUpload = async (e, mealIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        const meals = [...log.meals];
        meals[mealIndex].photo = base64;
        setLog({ ...log, meals });
        try {
          const res = await axios.post(`${API}/meals/analyze-photo`, {
            photo: base64,
            description: meals[mealIndex].description || meals[mealIndex].type,
          }, { headers: getHeaders() });
          setMealAnalysis(res.data.analysis);
          toast.success(language === 'de' ? 'Mahlzeit analysiert!' : 'Meal analyzed!');
        } catch { /* silent */ }
        setAnalyzing(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setAnalyzing(false);
    }
  };

  const dateStr = format(date, 'yyyy-MM-dd');

  const fetchLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tracking/log/${dateStr}`, { headers: getHeaders() });
      if (res.data && res.data.mood) {
        setLog({
          weight: res.data.weight || '',
          mood: res.data.mood || 3,
          energy: res.data.energy || 3,
          sleep_hours: res.data.sleep_hours || '',
          water_glasses: res.data.water_glasses || 0,
          steps: res.data.steps || '',
          meals: res.data.meals?.length ? res.data.meals : log.meals,
          habits: res.data.habits?.length ? res.data.habits : log.habits,
          notes: res.data.notes || '',
        });
      }
    } catch (e) { console.error(e); }
  }, [dateStr]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tracking/log`, {
        date: dateStr,
        weight: log.weight ? parseFloat(log.weight) : null,
        mood: log.mood,
        energy: log.energy,
        sleep_hours: log.sleep_hours ? parseFloat(log.sleep_hours) : null,
        water_glasses: log.water_glasses,
        steps: log.steps ? parseInt(log.steps) : null,
        meals: log.meals,
        habits: log.habits,
        notes: log.notes,
      }, { headers: getHeaders() });
      toast.success(t('tracking.saved'));
    } catch { toast.error(t('common.error')); }
    setSaving(false);
  };

  const mealTypeLabel = (type) => t(`tracking.${type}`);
  const habitLabels = { water_8: t('tracking.h_water'), veggies: t('tracking.h_veggies'), walk_10: t('tracking.h_walk'), mindful: t('tracking.h_mindful'), protein: t('tracking.h_protein') };

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-3xl" data-testid="tracking-page">
      <div>
        <button onClick={() => nav('/dashboard')} className="flex items-center gap-1 text-foreground/40 hover:text-foreground font-body text-xs mb-2 transition-colors" data-testid="tracking-back-btn">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t('tracking.title')}</h1>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="rounded-full border-border/80 font-body" data-testid="tracking-date-picker">
              <CalendarIcon size={16} className="mr-2" />
              {format(date, 'dd. MMM yyyy', { locale: language === 'de' ? de : undefined })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="single" selected={date} onSelect={d => d && setDate(d)} locale={language === 'de' ? de : undefined} />
          </PopoverContent>
        </Popover>
        </div>
      </div>

      {/* Mood & Energy */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="mood-energy-card">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <Label className="font-body text-sm font-medium text-foreground/70 mb-3 block">{t('tracking.mood')}</Label>
            <div className="flex gap-3">
              {moodIcons.map((icon, i) => (
                <button key={i} onClick={() => setLog({ ...log, mood: i + 1 })} className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all border-2 ${log.mood === i + 1 ? `${moodColors[i]} border-current scale-110 bg-current/10` : 'border-border/40 text-foreground/30 hover:border-foreground/20'}`} data-testid={`mood-${i + 1}`}>
                  {icon}
                </button>
              ))}
            </div>
            <p className="font-body text-xs text-foreground/40 mt-2">{t(`tracking.moods`)?.[log.mood - 1]}</p>
          </div>
          <div>
            <Label className="font-body text-sm font-medium text-foreground/70 mb-3 block">{t('tracking.energy')}</Label>
            <Slider value={[log.energy]} onValueChange={([v]) => setLog({ ...log, energy: v })} min={1} max={5} step={1} className="mt-4 [&_[role=slider]]:bg-terracotta [&_[role=slider]]:border-terracotta" data-testid="energy-slider" />
            <div className="flex justify-between mt-1">
              <span className="font-body text-xs text-foreground/30">1</span>
              <span className="font-body text-sm font-medium text-terracotta">{log.energy}/5</span>
              <span className="font-body text-xs text-foreground/30">5</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Weight, Sleep, Water, Steps */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="weight-input-card">
          <Label className="font-body text-xs font-medium text-foreground/60">{t('tracking.weight')}</Label>
          <Input type="number" step="0.1" value={log.weight} onChange={e => setLog({ ...log, weight: e.target.value })} className="mt-2 border-0 border-b-2 border-border/60 rounded-none px-0 font-display text-2xl font-semibold focus-visible:ring-0 focus-visible:border-terracotta" data-testid="weight-input" placeholder="--" />
        </Card>
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="sleep-input-card">
          <Label className="font-body text-xs font-medium text-foreground/60">{t('tracking.sleep')}</Label>
          <Input type="number" step="0.5" value={log.sleep_hours} onChange={e => setLog({ ...log, sleep_hours: e.target.value })} className="mt-2 border-0 border-b-2 border-border/60 rounded-none px-0 font-display text-2xl font-semibold focus-visible:ring-0 focus-visible:border-terracotta" data-testid="sleep-input" placeholder="--" />
        </Card>
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="water-input-card">
          <Label className="font-body text-xs font-medium text-foreground/60">{t('tracking.water')}</Label>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => setLog({ ...log, water_glasses: Math.max(0, log.water_glasses - 1) })} className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center hover:bg-warmgray transition-colors" data-testid="water-minus"><Minus size={14} /></button>
            <span className="font-display text-2xl font-semibold min-w-[2ch] text-center">{log.water_glasses}</span>
            <button onClick={() => setLog({ ...log, water_glasses: log.water_glasses + 1 })} className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center hover:bg-warmgray transition-colors" data-testid="water-plus"><Plus size={14} /></button>
            <Droplets size={16} className={`${log.water_glasses >= 8 ? 'text-blue-400' : 'text-foreground/20'}`} />
          </div>
        </Card>
        <Card className="bg-white border border-border/60 rounded-2xl p-5" data-testid="steps-input-card">
          <Label className="font-body text-xs font-medium text-foreground/60">{t('tracking.steps')}</Label>
          <Input type="number" value={log.steps} onChange={e => setLog({ ...log, steps: e.target.value })} className="mt-2 border-0 border-b-2 border-border/60 rounded-none px-0 font-display text-2xl font-semibold focus-visible:ring-0 focus-visible:border-terracotta" data-testid="steps-input" placeholder="--" />
        </Card>
      </div>

      {/* Meals - Photo First */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="meals-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-medium">{t('tracking.meals')}</h2>
          {analyzing && <div className="flex items-center gap-2 text-terracotta"><Loader2 size={16} className="animate-spin" /><span className="font-body text-xs">{language === 'de' ? 'Analysiere...' : 'Analyzing...'}</span></div>}
        </div>
        <div className="space-y-4">
          {log.meals.map((meal, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-start gap-3">
                <span className="font-body text-sm font-medium text-foreground/60 w-28 pt-2 shrink-0">{mealTypeLabel(meal.type)}</span>
                <Textarea value={meal.description} onChange={e => { const meals = [...log.meals]; meals[i].description = e.target.value; setLog({ ...log, meals }); }} placeholder={t('tracking.meal_desc')} className="min-h-[44px] resize-none rounded-xl border-border/60 font-body text-sm flex-1" rows={1} data-testid={`meal-${meal.type}-input`} />
                <label className="shrink-0 w-10 h-10 rounded-xl bg-terracotta/10 hover:bg-terracotta/20 flex items-center justify-center cursor-pointer transition-colors" data-testid={`meal-photo-${meal.type}`}>
                  <Camera size={18} className="text-terracotta" />
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handlePhotoUpload(e, i)} />
                </label>
              </div>
              {meal.photo && (
                <div className="ml-[7.5rem]">
                  <img src={meal.photo} alt={meal.type} className="w-20 h-20 rounded-xl object-cover border border-border/40" />
                </div>
              )}
            </div>
          ))}
        </div>
        {mealAnalysis && (
          <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200/50" data-testid="meal-analysis">
            <p className="font-body text-xs font-semibold text-amber-700 mb-1">{language === 'de' ? 'KI-Analyse' : 'AI Analysis'}</p>
            <p className="font-body text-sm text-foreground/70 whitespace-pre-line">{mealAnalysis}</p>
          </div>
        )}
      </Card>

      {/* Habits */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="habits-tracking-card">
        <h2 className="font-display text-xl font-medium mb-4">{t('tracking.habits_title')}</h2>
        <div className="space-y-3">
          {log.habits.map((h, i) => (
            <label key={i} className="flex items-center gap-3 cursor-pointer group" data-testid={`habit-${h.name}`}>
              <Checkbox checked={h.completed} onCheckedChange={checked => { const habits = [...log.habits]; habits[i].completed = checked; setLog({ ...log, habits }); }} className="data-[state=checked]:bg-sage data-[state=checked]:border-sage" />
              <span className={`font-body text-sm transition-colors ${h.completed ? 'text-sage font-medium line-through' : 'text-foreground/70 group-hover:text-foreground'}`}>
                {habitLabels[h.name] || h.name}
              </span>
            </label>
          ))}
        </div>
      </Card>

      {/* Notes */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="notes-card">
        <Label className="font-body text-sm font-medium text-foreground/70 mb-2 block">{t('tracking.notes')}</Label>
        <Textarea value={log.notes} onChange={e => setLog({ ...log, notes: e.target.value })} className="rounded-xl border-border/60 font-body" rows={3} data-testid="notes-input" />
      </Card>

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-10 py-5 text-base font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="save-tracking-btn">
        <Save size={18} className="mr-2" /> {saving ? '...' : t('tracking.save')}
      </Button>
    </div>
  );
}
